import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Spinner } from "react-bootstrap";
import {
  FaUsers,
  FaStore,
  FaUser,
  FaCreditCard,
  FaUserCheck,
  FaAd,
  FaHome,
  FaCog,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";

const SubSubAdminDashboard = () => {
  const { societyId } = useAuth();
  const navigate = useNavigate();
  const [subSubAdminName, setSubSubAdminName] = useState(() => {
    try {
      return (
        sessionStorage.getItem("QV_SUBSUBADMIN_NAME") ||
        sessionStorage.getItem("QV_NAME") ||
        sessionStorage.getItem("name") ||
        "Sub-Sub Admin"
      );
    } catch {
      return "Sub-Sub Admin";
    }
  });
  const [stats, setStats] = useState({
    totalVendors: 0,
    totalUsers: 0,
    totalSubscriptions: 0,
    pendingVerifications: 0,
    pendingAds: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let vendorsJson = null,
        usersJson = null,
        allVendorsJson = null,
        adsJson = null;
      let lastErr = null;
      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {};
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          console.log(`Fetching dashboard stats from: ${base}`);

          const [vendorsRes, usersRes, allVendorsRes, adsRes] =
            await Promise.all([
              fetch(`${base}/api/vendors`, { headers }).catch((err) => {
                console.error("Error fetching vendors:", err);
                return { ok: false };
              }),
              fetch(`${base}/api/users`, { headers }).catch((err) => {
                console.error("Error fetching users:", err);
                return { ok: false };
              }),
              fetch(`${base}/api/admins/verification-requests`, {
                headers,
              }).catch((err) => {
                console.error("Error fetching verification requests:", err);
                return { ok: false };
              }),
              fetch(`${base}/api/ads/pending`, { headers }).catch((err) => {
                console.error("Error fetching pending ads:", err);
                return { ok: false };
              }),
            ]);

          if ((!vendorsRes || !vendorsRes.ok) && (!usersRes || !usersRes.ok)) {
            lastErr = new Error("Failed core loads");
            continue;
          }

          if (vendorsRes && vendorsRes.ok) {
            vendorsJson = await vendorsRes.json();
          }
          if (usersRes && usersRes.ok) {
            usersJson = await usersRes.json();
          }
          if (allVendorsRes && allVendorsRes.ok) {
            try {
              allVendorsJson = await allVendorsRes.json();
            } catch {
              allVendorsJson = null;
            }
          }
          if (adsRes && adsRes.ok) {
            try {
              adsJson = await adsRes.json();
            } catch {
              adsJson = null;
            }
          }

          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }

      if (!vendorsJson && !usersJson)
        throw lastErr || new Error("Failed to load data");

      // Data already validated during fetch loop
      let vendorsArr = vendorsJson
        ? Array.isArray(vendorsJson)
          ? vendorsJson
          : vendorsJson.vendors || []
        : [];
      let usersArr = usersJson
        ? Array.isArray(usersJson)
          ? usersJson
          : usersJson.users || []
        : [];

      const userSubs = usersArr.reduce((acc, u) => {
        const sub = (u.subscription || "").toString().toLowerCase();
        return acc + (sub && sub !== "none" ? 1 : 0);
      }, 0);
      const vendorSubs = (Array.isArray(vendorsArr) ? vendorsArr : []).reduce(
        (acc, v) => {
          const sub = v.subscription;
          const hasSub =
            (typeof sub === "string" &&
              sub.toLowerCase() !== "none" &&
              sub.trim() !== "") ||
            (sub && typeof sub === "object");
          return acc + (hasSub ? 1 : 0);
        },
        0
      );

      // Count pending vendor verifications using the dedicated endpoint
      let pendingVerifications = 0;
      try {
        if (allVendorsJson && allVendorsJson.verificationRequests) {
          // Count only pending verification requests
          pendingVerifications = allVendorsJson.verificationRequests.filter(
            (vendor) => {
              const verificationStatus = vendor.verificationStatus;
              return verificationStatus === "pending" || !verificationStatus;
            }
          ).length;
        }
      } catch (error) {
        console.error(
          "Error fetching verification requests for dashboard:",
          error
        );
      }

      // Count pending ads
      let pendingAds = 0;
      try {
        if (adsJson && adsJson.ads) {
          pendingAds = adsJson.ads.length;
        }
      } catch (error) {
        console.error("Error fetching pending ads for dashboard:", error);
      }

      setStats((prev) => ({
        ...prev,
        totalVendors: vendorsArr.length,
        totalUsers: usersArr.length,
        totalSubscriptions: userSubs + vendorSubs,
        pendingVerifications,
        pendingAds,
      }));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setLoading(false);
    }
  };

  const dashboardItems = [
    {
      title: "Local Subscriptions",
      icon: <FaCreditCard size={24} />,
      description: "Manage subscriptions in your assigned society",
      path: "/local-subscriptions",
      count: stats.totalSubscriptions,
      color: "text-info",
      bgColor: "bg-info",
    },
    {
      title: "User Dashboard",
      icon: <FaUser size={24} />,
      description: "Manage users in your assigned society",
      path: "/user-dashboard",
      count: stats.totalUsers,
      color: "text-success",
      bgColor: "bg-success",
    },
    {
      title: "Vendor Dashboard",
      icon: <FaStore size={24} />,
      description: "Manage vendors in your assigned society",
      path: "/vendor-dashboard",
      count: stats.totalVendors,
      color: "text-primary",
      bgColor: "bg-primary",
    },
    {
      title: "Refer Friends",
      icon: <FaUsers size={24} />,
      description: "Refer new users and vendors to the platform",
      path: "/refer-friends",
      color: "text-warning",
      bgColor: "bg-warning",
    },
    {
      title: "Vendor Verification Requests",
      icon: <FaUserCheck size={24} />,
      description: "Review and process vendor verification requests",
      path: "/vendor-verification-requests",
      count: stats.pendingVerifications,
      color: "text-danger",
      bgColor: "bg-danger",
    },
    {
      title: "Ads Management",
      icon: <FaAd size={24} />,
      description: "Manage and review pending advertisements",
      path: "/ads-management",
      count: stats.pendingAds,
      color: "text-secondary",
      bgColor: "bg-secondary",
    },
  ];

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-4">
        {subSubAdminName
          ? `Welcome ${subSubAdminName}`
          : "Sub-Sub Admin Dashboard"}
      </h1>
      <Row>
        {dashboardItems.map((item, index) => (
          <Col md={6} lg={4} className="mb-4" key={index}>
            <Card
              className="h-100 cursor-pointer hover-shadow"
              onClick={() => navigate(item.path)}
              style={{ cursor: "pointer", minHeight: "150px" }}
            >
              <Card.Body className="d-flex flex-column align-items-center text-center justify-content-center">
                <div
                  className={`rounded-circle ${item.bgColor} d-flex align-items-center justify-content-center mx-auto mb-3`}
                  style={{ width: "60px", height: "60px" }}
                >
                  <div
                    className={
                      item.color.replace("text-", "text-") + " text-white"
                    }
                  >
                    {item.icon}
                  </div>
                </div>
                <Card.Title>{item.title}</Card.Title>
                {item.count !== undefined && (
                  <div className="fw-bold fs-4 text-primary">{item.count}</div>
                )}
                <Card.Text className="text-muted">{item.description}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        ))}

        <Col md={6} lg={4} className="mb-4">
          <Card className="h-100">
            <Card.Header className="bg-light">
              <h5 className="mb-0">System Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <FaHome className="text-primary me-2" size={20} />
                <div>
                  <div className="fw-semibold">System</div>
                  <div className="text-muted small">QuiVikme Admin Panel</div>
                </div>
              </div>
              <div className="d-flex align-items-center mb-3">
                <FaUserCheck className="text-success me-2" size={20} />
                <div>
                  <div className="fw-semibold">User Role</div>
                  <div className="text-muted small">Sub-Sub Admin</div>
                </div>
              </div>
              <div className="d-flex align-items-center mb-3">
                <FaCog className="text-info me-2" size={20} />
                <div>
                  <div className="fw-semibold">System Status</div>
                  <div className="text-muted small">Operational</div>
                </div>
              </div>
              <div className="d-grid gap-2 mt-3">
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    // Clear all session and local storage
                    sessionStorage.clear();
                    localStorage.removeItem("adminToken");
                    // Redirect to login page
                    window.location.href = "/admin";
                  }}
                >
                  <FaUser className="me-2" />
                  Logout
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="mt-4 p-3 bg-light rounded">
        <h5>Sub-Sub Admin Role</h5>
        <p className="mb-0">
          As a Sub-Sub Admin, you can manage content within your assigned
          society. Your access is limited to the specific society you were
          assigned to by your parent Sub-Admin.
        </p>
      </div>

      <style jsx>{`
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
          transition: all 0.2s ease-in-out;
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default SubSubAdminDashboard;
